<template>
  <!--
    „Neue Adresse anlegen“ (Kundenfeedback 18.10.2026, XIII): allgemeines
    Formular ohne Typ-Zwang – die Adresse landet als allgemeiner Kontakt im
    Adressbuch und wird nur in dieser Akte mit der gewählten Rolle verknüpft
    (optional gleich in dieser Rolle eingeladen).
  -->
  <form class="naf" data-testid="apd-new-address" @submit.prevent="submit">
    <div class="naf__head">
      <h5 class="naf__title">{{ $gt('Neue Adresse anlegen') }}</h5>
      <button type="button" class="naf__back" @click="$emit('cancel')">{{ $gt('Zurück zur Suche') }}</button>
    </div>
    <p class="naf__hint">{{ $gt('Die Adresse wird in Ihren eigenen Kontakten gespeichert und nur in dieser Akte mit der Rolle verknüpft.') }}</p>

    <div class="naf__grid">
      <label class="naf__field naf__field--wide">
        <span>{{ $gt('Name oder Firma') }} *</span>
        <input v-model.trim="form.name" class="naf__input" required data-testid="apd-new-name" />
      </label>
      <label class="naf__field naf__field--wide">
        <span>{{ $gt('Ansprechpartner') }}</span>
        <input v-model.trim="form.contactPerson" class="naf__input" :placeholder="$gt('Vorname Nachname')" />
      </label>
      <label class="naf__field naf__field--wide">
        <span>{{ $gt('Straße und Hausnummer') }}</span>
        <input v-model.trim="form.street" class="naf__input" />
      </label>
      <label class="naf__field naf__field--zip">
        <span>{{ $gt('PLZ') }}</span>
        <input v-model.trim="form.zip" class="naf__input" inputmode="numeric" maxlength="10" />
      </label>
      <label class="naf__field naf__field--city">
        <span>{{ $gt('Ort') }}</span>
        <input v-model.trim="form.city" class="naf__input" />
      </label>
      <label class="naf__field naf__field--email">
        <span>{{ $gt('E-Mail') }}</span>
        <input v-model.trim="form.email" class="naf__input" type="email" data-testid="apd-new-email" />
        <small v-if="emailInvalid" class="naf__error">{{ $gt('Bitte eine gültige E-Mail-Adresse eingeben.') }}</small>
        <!-- Kundenfeedback 03.11.2026 (I): „Wenn ich selbst einen Kontakt angelegt
             hatte, versteht KARS, dass das möglicherweise derselbe Kontakt ist?“ -->
        <span v-if="duplicateContact" class="naf__dup" data-testid="apd-new-duplicate">
          <span class="naf__dup-chip">{{ $gt('Bereits als Kontakt angelegt') }}</span>
          <span class="naf__dup-name">{{ duplicateName }}</span>
          <button type="button" class="naf__dup-use" data-testid="apd-new-use-contact" @click="$emit('use-contact', { contact: duplicateContact, role: form.role })">
            {{ $gt('Vorhandenen Kontakt zuordnen') }}
          </button>
        </span>
      </label>
      <label class="naf__field naf__field--phone">
        <span>{{ $gt('Telefon') }}</span>
        <input v-model.trim="form.phone" class="naf__input" type="tel" />
      </label>
    </div>

    <div class="naf__assign">
      <label class="naf__field naf__field--inline">
        <span>{{ $gt('In dieser Akte als') }}</span>
        <select v-model="form.role" class="naf__input naf__select" data-testid="apd-new-role">
          <option v-for="r in roleChoices" :key="r.value" :value="r.value">{{ r.label }}</option>
        </select>
      </label>
      <label class="naf__check" :class="{ 'naf__check--off': !canInvite }">
        <input v-model="form.invite" type="checkbox" :disabled="!canInvite" data-testid="apd-new-invite" />
        <span>{{ inviteLabel }}</span>
      </label>
    </div>

    <div class="naf__foot">
      <button type="button" class="naf__cancel" @click="$emit('cancel')">{{ $gt('Abbrechen') }}</button>
      <button type="submit" class="naf__submit" :disabled="!canSubmit" data-testid="apd-new-submit">
        <v-progress-circular v-if="busy" indeterminate size="13" width="2" color="white" />
        {{ $gt('Anlegen und zuordnen') }}
      </button>
    </div>
  </form>
</template>

<script>
import { emailRule } from "@/utils/validationRules";
import { findContactByEmail } from "@/utils/contactSearchHits";
import { getContactDisplayName } from "@/services/contactModel";
import { MANDANT_ROLE_KEY, normalizeRole } from "./participantDialogModel";

const NOT_INVITABLE = new Set(["Versicherung", "Versicherungsmakler", MANDANT_ROLE_KEY]);

export default {
  name: "NewAddressForm",

  props: {
    initialName: { type: String, default: "" },
    initialRole: { type: String, default: "" },
    roleChoices: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    /** Eigene Kontakte für den Dublettenhinweis (gleiche E-Mail-Adresse). */
    knownContacts: { type: Array, default: () => [] },
  },

  emits: ["submit", "cancel", "use-contact"],

  data() {
    return {
      form: {
        name: this.initialName,
        contactPerson: "",
        street: "",
        zip: "",
        city: "",
        email: "",
        phone: "",
        role: normalizeRole(this.initialRole),
        invite: false,
      },
    };
  },

  computed: {
    emailInvalid() { return !!this.form.email && emailRule()(this.form.email) !== true; },
    canInvite() { return !!this.form.email && !this.emailInvalid && !NOT_INVITABLE.has(this.form.role); },
    inviteLabel() {
      if (NOT_INVITABLE.has(this.form.role)) return this.$gt("Für diese Rolle keine Einladung");
      if (!this.form.email) return this.$gt("Einladen (E-Mail-Adresse erforderlich)");
      return this.$gt("In dieser Rolle zur Akte einladen");
    },
    canSubmit() { return !!this.form.name && !this.emailInvalid && !this.busy; },
    duplicateContact() { return this.emailInvalid ? null : findContactByEmail(this.knownContacts, this.form.email); },
    duplicateName() { return this.duplicateContact ? getContactDisplayName(this.duplicateContact) : ""; },
  },

  watch: {
    canInvite(ok) { if (!ok) this.form.invite = false; },
  },

  methods: {
    splitPerson(value) {
      const parts = (value || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return { firstName: null, lastName: null };
      if (parts.length === 1) return { firstName: null, lastName: parts[0] };
      return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] };
    },
    submit() {
      if (!this.canSubmit) return;
      const person = this.splitPerson(this.form.contactPerson);
      this.$emit("submit", {
        contact: {
          companyName: this.form.name,
          firstName: person.firstName,
          lastName: person.lastName,
          streetNo: this.form.street || null,
          zip: this.form.zip || null,
          city: this.form.city || null,
          email: this.form.email || null,
          phone: this.form.phone || null,
        },
        role: this.form.role,
        invite: this.canInvite && this.form.invite,
      });
    },
  },
};
</script>

<style scoped>
.naf { display: flex; flex-direction: column; gap: 12px; padding: 16px 18px; background: #fff; border: 1px solid var(--border-grey-color-light); border-radius: 13px; }
.naf__head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.naf__title { margin: 0; font-size: 15px; font-weight: 700; }
.naf__back { border: none; background: none; padding: 0; font: inherit; font-size: 12.5px; color: var(--primary-color); cursor: pointer; text-decoration: underline; }
.naf__hint { margin: -6px 0 0; font-size: 12px; color: var(--grey-color); }
.naf__grid { display: grid; grid-template-columns: 110px 1fr 1fr 1fr; gap: 10px 12px; }
.naf__field { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: #374151; grid-column: span 1; }
.naf__field--wide { grid-column: 1 / -1; }
.naf__field--zip { grid-column: 1 / 2; }
.naf__field--city { grid-column: 2 / 5; }
.naf__field--email { grid-column: 1 / 3; }
.naf__field--phone { grid-column: 3 / 5; }
.naf__field--inline { flex-direction: row; align-items: center; gap: 8px; }
.naf__input {
  height: 34px; padding: 0 10px; border: 1px solid var(--border-grey-color); border-radius: 9px; background: #fff;
  font: inherit; font-size: 13px; font-weight: 400; color: var(--dark-color);
}
.naf__input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px var(--primary-light-color-transparent); }
.naf__select { min-width: 220px; cursor: pointer; }
.naf__error { font-weight: 500; color: #b42318; }
.naf__dup { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px; margin-top: 2px; font-weight: 500; }
.naf__dup-chip { padding: 2px 8px; border-radius: 999px; border: 1px solid #f5c26b; background: #fff8eb; color: #92400e; font-size: 11px; font-weight: 600; white-space: nowrap; }
.naf__dup-name { color: var(--dark-color); font-size: 12px; }
.naf__dup-use { border: none; background: none; padding: 0; font: inherit; font-size: 12px; font-weight: 600; color: var(--primary-color); cursor: pointer; text-decoration: underline; }
.naf__dup-use:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; border-radius: 4px; }
.naf__assign { display: flex; align-items: center; flex-wrap: wrap; gap: 14px 20px; padding: 10px 12px; border-radius: 10px; border: 1px solid var(--border-grey-color-light); background: #fff; }
.naf__check { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--dark-color); cursor: pointer; }
.naf__check--off { color: var(--grey-color); cursor: default; }
.naf__foot { display: flex; justify-content: flex-end; gap: 8px; }
.naf__cancel, .naf__submit { height: 36px; padding: 0 16px; border-radius: 10px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.naf__cancel { border: 1px solid var(--border-grey-color); background: #fff; color: var(--grey-color); }
.naf__cancel:hover { color: var(--primary-color); border-color: var(--primary-color); }
.naf__submit { display: inline-flex; align-items: center; gap: 7px; border: none; background: var(--primary-color); color: #fff; }
.naf__submit:disabled { opacity: .5; cursor: not-allowed; }
.naf__submit:focus-visible, .naf__cancel:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
@media (max-width: 760px) { .naf__grid { grid-template-columns: 1fr; } .naf__field { grid-column: 1 / -1 !important; } }
</style>
